import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  SORT_BY_PARAM,
  CURRENT_PAGE_PARAM,
  CATEGORY_ID_PARAM,
  SEARCH_QUERY_PARAM,
  FILTER_BY_EQUIPMENT_PARAM,
  SERIAL_NUMBER_PARAM,
  MODEL_PARAM,
  FACET_PARAM,
  IS_NER_ENABLED,
  RESPONSE_FILTER_PARAM,
  FILTER_BY_ATTACHMENT_PARAM,
  PRODUCT_FAMILY_NAME_PARAM
} from './constants/common';
import { decodeString } from '@cat-ecom/common/utils/stringUtils';
import { SearchParams } from 'app/[lang]/[esite]/(core)/(search-display)/types';
import { SearchParams as CategorySearchParams } from 'app/[lang]/[esite]/(core)/category/[category]/types';
import queryString from 'query-string';
import { getNewResponseFilterParam } from './utils';
import { RESPONSE_FILTER } from 'constants/common';

export default function useFilter() {
  /*  TODO - Input Validations - useSearchParams: The search param object should contain key value pairs where
  the value is of string type, not including reservered url special / unsafe characters like / \ & ? = : % + ' " ; < > { } [ ] @ #
  */
  const params = useSearchParams();
  /*
    TODO - Input Validations - usePathName: string except the
    generalRestrictedCharacters (@\constants\securityConstants.ts)
  */
  const pathname = usePathname();
  const router = useRouter();
  const options = { scroll: false };

  const handleSortBy = (order: string, sortByParam: string = SORT_BY_PARAM) => {
    const newParams = new URLSearchParams(params.toString());
    newParams.set(sortByParam, order);
    newParams.set(CURRENT_PAGE_PARAM, '1');
    const newURL = `${pathname}?${newParams.toString()}`;
    router.replace(newURL, options);
  };

  const handleResetFilters = (params: SearchParams | CategorySearchParams) => {
    const stringifiedParams = queryString.stringify(params, {
      encode: false,
      sort: false
    });
    const newParams = new URLSearchParams(stringifiedParams);
    const newURL = `${pathname}?${newParams.toString()}`;
    router.replace(newURL, options);
  };

  const handleCategoryPLP = (subcategory: string) => {
    const newParams = new URLSearchParams(params.toString());
    newParams.set(CURRENT_PAGE_PARAM, '1');
    newParams.set(SORT_BY_PARAM, '0');
    newParams.delete(FACET_PARAM);
    const newURL = `${pathname.split('/', 4).join('/')}/${subcategory}?${newParams}`;
    router.push(newURL);
  };

  const handleSortByCategory = (categoryId: string) => {
    const id = decodeString(categoryId);
    const newParams = new URLSearchParams(params.toString());
    newParams.set(CATEGORY_ID_PARAM, id);
    newParams.set(CURRENT_PAGE_PARAM, '1');
    newParams.delete(FACET_PARAM);
    const newURL = `${pathname}?${newParams.toString()}`;
    router.push(newURL);
  };

  const handleSearchSuggestion = (query: string) => {
    const newParams = new URLSearchParams(params.toString());
    newParams.set(SEARCH_QUERY_PARAM, query);
    newParams.set(CURRENT_PAGE_PARAM, '1');
    const newURL = `${pathname}?${newParams.toString()}`;
    router.push(newURL);
  };

  const handleSortByEquipment = ({
    checked,
    model,
    serialNumber,
    isSearchPage,
    shouldDeleteEquipmentParams
  }: {
    checked: boolean;
    model?: string;
    serialNumber?: string;
    isSearchPage?: boolean;
    shouldDeleteEquipmentParams?: boolean;
  }) => {
    const newParams = new URLSearchParams(params.toString());
    if (checked) {
      newParams.set(FILTER_BY_EQUIPMENT_PARAM, checked.toString());
      newParams.set(MODEL_PARAM, model);
      if (newParams.has(PRODUCT_FAMILY_NAME_PARAM)) {
        newParams.delete(PRODUCT_FAMILY_NAME_PARAM);
      }
      if (serialNumber) {
        newParams.set(SERIAL_NUMBER_PARAM, serialNumber);
      } else {
        newParams.delete(SERIAL_NUMBER_PARAM);
      }
      if (isSearchPage) {
        newParams.set(IS_NER_ENABLED, 'false');
      }
    } else {
      if (shouldDeleteEquipmentParams) {
        newParams.delete(FILTER_BY_EQUIPMENT_PARAM);
        newParams.delete(MODEL_PARAM);
        newParams.delete(SERIAL_NUMBER_PARAM);
        newParams.delete(PRODUCT_FAMILY_NAME_PARAM);
      } else if (newParams.get(FILTER_BY_EQUIPMENT_PARAM)) {
        newParams.set(FILTER_BY_EQUIPMENT_PARAM, checked.toString());
      }
      if (newParams.has(IS_NER_ENABLED)) {
        newParams.delete(IS_NER_ENABLED);
      }
      if (newParams.has(RESPONSE_FILTER_PARAM)) {
        newParams.set(RESPONSE_FILTER_PARAM, RESPONSE_FILTER.PARTS);
      }
    }
    newParams.set(CURRENT_PAGE_PARAM, '1');

    const newURL = `${pathname}?${newParams.toString()}`;
    router.replace(newURL, options);
  };

  const handleSortByFacet = (
    facetId: string,
    checked: boolean,
    facetParam = FACET_PARAM
  ) => {
    const id = decodeString(facetId);
    const newParams = new URLSearchParams(params.toString());

    if (checked) {
      newParams.append(facetParam, id);
    } else {
      newParams.delete(facetParam, id);
    }
    newParams.set(CURRENT_PAGE_PARAM, '1');

    const newURL = `${pathname}?${newParams.toString()}`;
    router.push(newURL, options);
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(params.toString());
    newParams.set(CURRENT_PAGE_PARAM, String(page));
    const newURL = `${pathname}?${newParams.toString()}`;
    router.push(newURL);
  };

  const handleClearAllFilters = (facetId: string | string[]) => {
    const newParams = new URLSearchParams(params.toString());
    let id: string;

    if (typeof facetId !== 'string') {
      facetId.map((facet: string) => {
        id = decodeString(facet);
        newParams.delete(FACET_PARAM, id);
      });
    } else {
      id = decodeString(facetId);
      newParams.delete(FACET_PARAM, id);
    }
    const newURL = `${pathname}?${newParams.toString()}`;
    router.replace(newURL, options);
  };

  const handleSortByUomChange = (
    facetsToDelete: { uom: string; facet: string }[]
  ) => {
    let id: string;
    const newParams = new URLSearchParams(params.toString());

    facetsToDelete.map((facet) => {
      id = decodeString(facet.facet);
      newParams.delete(FACET_PARAM, id);
    });

    newParams.set(CURRENT_PAGE_PARAM, '1');
    const newURL = `${pathname}?${newParams.toString()}`;
    router.replace(newURL, options);
  };
  const handleSortByNER = (isNEREnabled: boolean) => {
    const newParams = new URLSearchParams(params.toString());
    newParams.set(IS_NER_ENABLED, isNEREnabled.toString());
    const newURL = `${pathname}?${newParams.toString()}`;
    router.replace(newURL, options);
  };

  const handleFederatedResponseFilter = () => {
    const newParams = new URLSearchParams(params.toString());
    const responseFilterParam = newParams.get(RESPONSE_FILTER_PARAM);
    newParams.set(
      RESPONSE_FILTER_PARAM,
      getNewResponseFilterParam(responseFilterParam)
    );
    const newURL = `${pathname}?${newParams.toString()}`;
    router.push(newURL, options);
  };

  const filterByAttachment = (checked: boolean) => {
    const newParams = new URLSearchParams(params.toString());
    if (checked) {
      newParams.set(FILTER_BY_ATTACHMENT_PARAM, 'true');
    } else {
      newParams.set(FILTER_BY_ATTACHMENT_PARAM, 'false');
    }
    const newURL = `${pathname}?${newParams.toString()}`;
    router.push(newURL, options);
  };

  return {
    handleSortBy,
    handleCategoryPLP,
    handleClearAllFilters,
    handleSortByCategory,
    handleSortByEquipment,
    handleSearchSuggestion,
    handleSortByFacet,
    handlePageChange,
    handleSortByUomChange,
    handleResetFilters,
    handleSortByNER,
    handleFederatedResponseFilter,
    filterByAttachment
  };
}
